export type ViewKey =
    | 'home'
    | 'work'
    | 'project'
    | 'studio'
    | 'contact'
    | 'void'

export interface CameraParams {
    // Camera radius, in Schwarzschild radii (R)
    distance: number
    // Degrees above the plane of the disk
    inclination: number
    // Degrees around the spin axis
    azimuth: number
    // Degrees of camera roll
    roll: number
    // Vertical field of view in degrees, for landscape screens
    fov: number
    // Where the hole sits on screen, as a fraction of half the width and half the height
    shiftX: number
    shiftY: number
    // Light gains
    disk: number
    ring: number
    stars: number
    jet: number
    exposure: number
    // Long soft streaks drawn out of the front of the disk on near edge-on views
    streak: number
}

export interface ViewDefinition {
    base: CameraParams
    // Where the camera drifts to at the bottom of the page, used when the page has no camera anchors
    end?: Partial<CameraParams>
    // Named camera positions, picked up from sections marked with data-camera
    anchors?: Record<string, Partial<CameraParams>>
    // Applied last on screens under 768px wide, for viewpoints that depend on the exact framing
    compact?: Partial<CameraParams>
}

// Every page looks at the same object from its own place. The camera never turns around the
// hole while a page is read: scrolling only brings it closer, further, or slides the frame.
export const VIEWS: Record<ViewKey, ViewDefinition> = {
    // Home: edge on and enormous, the disk drawn out into long gold streaks across the window.
    // It drifts closer through the three beats, steps back to the left for the work, then falls
    // away to a small distant object at the bottom of the last screen.
    home: {
        base: {
            distance: 15,
            inclination: 3.2,
            azimuth: 0,
            roll: -1.5,
            fov: 34,
            shiftX: 0.1,
            shiftY: 0.1,
            disk: 1,
            ring: 1,
            stars: 1,
            jet: 0.3,
            exposure: 1,
            streak: 1
        },
        anchors: {
            opening: {},
            beat1: {
                distance: 13.4,
                inclination: 3.8,
                shiftX: 0.4,
                shiftY: 0.06
            },
            beat2: {
                distance: 11.8,
                inclination: 4.6,
                shiftX: 0.44,
                shiftY: 0.04
            },
            beat3: {
                distance: 10.2,
                inclination: 5.4,
                fov: 36,
                shiftX: 0.48,
                shiftY: 0.02
            },
            work1: {
                distance: 26,
                inclination: 6,
                roll: 0,
                fov: 34,
                shiftX: -0.62,
                shiftY: 0.08,
                streak: 0.75
            },
            work2: {
                distance: 28,
                inclination: 6.5,
                roll: 0,
                fov: 34,
                shiftX: -0.64,
                shiftY: -0.02,
                streak: 0.75
            },
            work3: {
                distance: 30,
                inclination: 7,
                roll: 0,
                fov: 34,
                shiftX: -0.66,
                shiftY: 0.04,
                streak: 0.75
            },
            end: {
                distance: 74,
                inclination: 4.5,
                roll: 0,
                fov: 30,
                shiftX: 0,
                shiftY: -0.66,
                jet: 0.4,
                exposure: 1.05,
                streak: 0.6
            }
        }
    },
    // Work: far away and small in the upper right, a faint jet above and below it
    work: {
        base: {
            distance: 120,
            inclination: 14,
            azimuth: 35,
            roll: -3,
            fov: 32,
            shiftX: 0.62,
            shiftY: 0.58,
            disk: 1.05,
            ring: 1.05,
            stars: 0.9,
            jet: 0.55,
            exposure: 1.1,
            streak: 0.25
        },
        end: {
            distance: 104,
            shiftY: 0.62
        },
        compact: {
            shiftY: 0.7
        }
    },
    // Project: from above, the whole disk as a dim glowing circle with the shadow at its centre
    project: {
        base: {
            distance: 40,
            inclination: 80,
            azimuth: -25,
            roll: 0,
            fov: 42,
            shiftX: 0.36,
            shiftY: -0.1,
            disk: 0.55,
            ring: 0.65,
            stars: 0.55,
            jet: 0,
            exposure: 0.8,
            streak: 0
        },
        end: {
            distance: 50,
            shiftX: 0.1,
            shiftY: 0.12
        },
        compact: {
            shiftY: 0.2
        }
    },
    // Studio: so close that the hole sits above the top edge and only the lower curve of the
    // photon ring shows, a thin glowing horizon along the top of the window
    studio: {
        base: {
            distance: 3.6,
            inclination: 62,
            azimuth: 120,
            roll: 0,
            fov: 44,
            shiftX: 0,
            shiftY: 2.76,
            disk: 0.1,
            ring: 1.3,
            stars: 0.55,
            jet: 0,
            exposure: 1,
            streak: 0
        },
        end: {
            shiftY: 2.7
        },
        compact: {
            distance: 3.6,
            shiftY: 1.9
        }
    },
    // Contact: closer still, face on, the shadow filling the window and the ring wrapped around it
    contact: {
        base: {
            distance: 4,
            inclination: 84,
            azimuth: 200,
            roll: 0,
            fov: 68,
            shiftX: 0,
            shiftY: 0,
            disk: 0.3,
            ring: 1.35,
            stars: 0.45,
            jet: 0,
            exposure: 1,
            streak: 0
        },
        compact: {
            distance: 4
        }
    },
    void: {
        base: {
            distance: 80,
            inclination: 6,
            azimuth: 210,
            roll: 0,
            fov: 34,
            shiftX: 0.3,
            shiftY: 0.1,
            disk: 0.7,
            ring: 0.85,
            stars: 1,
            jet: 0.3,
            exposure: 1,
            streak: 0.4
        }
    }
}

// The menu keeps the azimuth of the page it opened over and moves the hole, small and quiet, to the right
export const MENU_VIEW: Partial<CameraParams> = {
    distance: 75,
    inclination: 9,
    roll: 0,
    fov: 34,
    shiftX: 0.55,
    shiftY: 0.02,
    disk: 0.7,
    ring: 0.85,
    stars: 0.7,
    jet: 0.25,
    exposure: 0.85,
    streak: 0.35
}

export const CAMERA_KEYS: Array<keyof CameraParams> = [
    'distance',
    'inclination',
    'azimuth',
    'roll',
    'fov',
    'shiftX',
    'shiftY',
    'disk',
    'ring',
    'stars',
    'jet',
    'exposure',
    'streak'
]

export const viewForPath = (path: string): ViewKey => {
    const clean = path.split(/[?#]/)[0].replace(/\/+$/, '') || '/'

    if (clean === '/') return 'home'
    if (clean === '/work') return 'work'
    if (clean.startsWith('/work/')) return 'project'
    if (clean === '/studio') return 'studio'
    if (clean === '/contact') return 'contact'

    return 'void'
}
